import type { SeoMeta } from "./seo";

const DEFAULT_OG_IMAGE = "https://www.capture.com/opengraph.jpg";

/**
 * True when the current document's <head> was resolved by the server for the
 * landing URL. The browser must not rewrite that head on first load: it is
 * what Google indexed, and the browser could only reproduce it from the same
 * inputs anyway. Set by entry-client before hydration.
 */
let serverHeadUrl: string | null = null;

export function markServerRenderedHead(url: string): void {
  serverHeadUrl = url;
}

/** Returns true once for the landing URL when the server already wrote its head. */
export function consumeServerRenderedHead(url: string): boolean {
  if (serverHeadUrl === null || serverHeadUrl !== url) {
    serverHeadUrl = null;
    return false;
  }
  serverHeadUrl = null;
  return true;
}

/** Writes title, description, canonical, robots, og:* and twitter:* tags. */
export function applyHeadMeta(meta: SeoMeta): void {
  document.title = meta.title;
  document.querySelector('meta[name="description"]')?.setAttribute("content", meta.description);
  if (meta.canonical) {
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", meta.canonical);
  }
  document.querySelector('meta[name="robots"]')?.setAttribute("content", meta.robots ?? "index, follow");
  const socialMeta: Record<string, string> = {
    "og:title": meta.title,
    "og:description": meta.description,
    "og:url": meta.canonical,
    "og:image": meta.image ?? DEFAULT_OG_IMAGE,
    "og:type": meta.ogType ?? "website",
    "twitter:url": meta.canonical,
    "twitter:title": meta.title,
    "twitter:description": meta.description,
    "twitter:image": meta.image ?? DEFAULT_OG_IMAGE,
  };
  for (const [key, value] of Object.entries(socialMeta)) {
    const selector = key.startsWith("og:") ? `meta[property="${key}"]` : `meta[name="${key}"]`;
    document.querySelector(selector)?.setAttribute("content", value);
  }
}
